import {useCallback, useMemo, useRef} from 'react'
import {firstMatch, findClosest} from 'utils/sortedArray'
import useOrderbook from './useOrderbook'
import {mediaJs} from '../utils/media'

const MIN_SCALE_NUM = 10

function useDepth (symbol, width, height, scale, cursor, veritcal) {
  const halfWidth = width / 2
  const symbolRef = useRef()
  const minScaleRef = useRef()
  const maxScaleRef = useRef()

  if (symbolRef.current !== symbol) {
    symbolRef.current = symbol
    minScaleRef.current = null
    maxScaleRef.current = null
  }

  const rawAsks = useOrderbook({
    symbol,
    type: 'ask',
    mergeDesc: false,
    orderDesc: false,
    tickSize: 1
  })

  const rawBids = useOrderbook({
    symbol,
    type: 'bid',
    mergeDesc: true,
    orderDesc: true,
    tickSize: 1
  })

  const asks = useMemo(() => {
    let depth = 0
    const orders = []
    rawAsks.forEach(ord => {
      depth += ord.amount
      depth = depth.toFixed(2) * 1
      orders.push({...ord, depth})
    })
    return orders
  }, [rawAsks])

  const bids = useMemo(() => {
    let depth = 0
    const orders = []
    rawBids.forEach(ord => {
      depth += ord.amount
      depth = depth.toFixed(2) * 1
      orders.push({...ord, depth})
    })
    return orders
  }, [rawBids])

  const {middlePrice, priceScales} = useMemo(() => {
    const askLowPrice = Number(asks[0]?.price)
    const askHighPrice = Number(asks[asks.length - 1]?.price)
    const askPriceAtIndex = Number(asks[MIN_SCALE_NUM - 1]?.price)

    const bidHighPrice = Number(bids[0]?.price)
    const bidLowPrice = Number(bids[bids.length - 1]?.price)
    const bidPriceAtIndex = Number(bids[MIN_SCALE_NUM - 1]?.price)

    const _middlePrice = (bidHighPrice + askLowPrice) / 2

    const minPriceDiff = Math.max(
      (askPriceAtIndex || askHighPrice) - askLowPrice,
      bidHighPrice - (bidPriceAtIndex || bidLowPrice)
    )
    const maxPriceDiff = Math.max(askHighPrice - askLowPrice, bidHighPrice - bidLowPrice)

    let _minScale = 1 / maxPriceDiff
    if (!minScaleRef.current || _minScale > minScaleRef.current) {
      minScaleRef.current = _minScale
    }
    let _maxScale = 1 / minPriceDiff
    if (!maxScaleRef.current || _maxScale > maxScaleRef.current) {
      maxScaleRef.current = _maxScale
    }

    _minScale = minScaleRef.current
    _maxScale = maxScaleRef.current
    const _priceScales = _minScale + ((_maxScale - _minScale) / 1000) * scale

    return {
      middlePrice: _middlePrice,
      priceScales: _priceScales
    }
  }, [asks, bids, scale])

  const pricePos = useCallback(
    price => (Number(price) - middlePrice) * priceScales * halfWidth + halfWidth,
    [halfWidth, middlePrice, priceScales]
  )

  const priceAtPos = useCallback(pos => (pos - halfWidth) / halfWidth / priceScales + middlePrice, [
    halfWidth,
    middlePrice,
    priceScales
  ])

  const maxBidDepthIdx = useMemo(() => firstMatch(bids, d => pricePos(d.price) < 0), [
    bids,
    pricePos
  ])
  const maxBidDepth = bids[maxBidDepthIdx || bids.length - 1]?.depth
  const maxAskDepthIdx = useMemo(() => firstMatch(asks, d => pricePos(d.price) > width), [
    asks,
    pricePos,
    width
  ])
  const maxAskDepth = asks[maxAskDepthIdx || asks.length - 1]?.depth
  const maxDepth = Math.max(maxBidDepth, maxAskDepth)

  const depthPos = useCallback(
    depth => {
      const coeff = veritcal ? 0.9 : 0.6
      return height - (depth / maxDepth) * coeff * height - 1
    },
    [veritcal, height, maxDepth]
  )

  const filteredAsks = asks.slice(0, maxAskDepthIdx ? maxAskDepthIdx + 1 : asks.length)
  const filteredBids = bids.slice(0, maxBidDepthIdx ? maxBidDepthIdx + 1 : bids.length)

  const {ask, bid} = useMemo(() => {
    let _ask
    let _bid

    if (cursor.x && filteredAsks.length) {
      let mirrorPrice
      if (cursor.x > halfWidth) {
        _ask = findClosest(filteredAsks, v => v?.price, priceAtPos(cursor.x))
        mirrorPrice = middlePrice * 2 - _ask?.price
        _bid = findClosest(filteredBids, v => -v?.price, -mirrorPrice)
      } else {
        _bid = findClosest(filteredBids, v => -v?.price, -priceAtPos(cursor.x))
        mirrorPrice = middlePrice * 2 - _bid?.price
        _ask = findClosest(filteredAsks, v => v?.price, mirrorPrice)
      }
    }
    return {
      ask: _ask,
      bid: _bid
    }
  }, [cursor.x, filteredAsks, filteredBids, halfWidth, middlePrice, priceAtPos])

  const askX = (ask && pricePos(ask.price)) || 0
  const askY = (ask && depthPos(ask.depth)) || 0
  const bidX = (bid && pricePos(bid.price)) || 0
  const bidY = (bid && depthPos(bid.depth)) || 0

  const scaleArray = useMemo(() => {
    const _scale = mediaJs('tablet') ? 5 : 10
    const beginPrice = priceAtPos(0)
    const endPrice = priceAtPos(width)
    const scaleValue = (endPrice - beginPrice) / _scale
    const array = []
    let price = beginPrice + scaleValue / 2
    for (let i = 0; i < _scale; i++) {
      if (price) {
        if (Number(price) > 0) array.push(price)
        price += scaleValue
      }
    }
    return array
  }, [width, priceAtPos])

  return {
    asks: filteredAsks,
    bids: filteredBids,
    pricePos,
    depthPos,
    currentAsk: ask,
    currentBid: bid,
    askX,
    askY,
    bidX,
    bidY,
    scaleArray
  }
}

export default useDepth
